import { DefaultLoader } from '@euler/app/components/loaders/DefaultLoader';
import { FooterView } from '@euler/app/flows/home/components/FooterView';
import { useTaskCardMetrics } from '@euler/app/flows/home/components/TaskCard';
import { Center } from '@euler/components/Center';
import { EmptyView } from '@euler/components/EmptyView';
import { computeGridLayoutItemSize } from '@euler/functions/layoutMetrics';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { SearchOrderResultItem } from '@euler/model/order/SearchOrderResultItem';
import { safeMarkDevFlag } from '@euler/utils';
import {
  ReactNode,
  forwardRef,
  memo,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import {
  ActivityIndicator,
  FlatListProps,
  ListRenderItem,
  NativeScrollEvent,
  NativeSyntheticEvent,
  Platform,
  RefreshControl as RNRefreshControl,
  StyleProp,
  ViewStyle,
} from 'react-native';
import { FlatList, createNativeWrapper } from 'react-native-gesture-handler';
import { useSharedValue } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { OrderSearchResultListItem } from './OrderSearchResultListItem';

type Item = SearchOrderResultItem;

export type OrderSearchResultListRef = {
  scrollToTop: (animated?: boolean) => void;
  getContentOffset: () => number;
};

const kMarginMultiColumn = 7.5;
const kMarginSingleColumn = 6.5;

const kFakeEmpty = safeMarkDevFlag(false);

const keyExtractor = (item: Item) => item.orderNo;

// see https://github.com/software-mansion/react-native-gesture-handler/issues/1067
// and this pull request probably will solve this problem:
// https://github.com/software-mansion/react-native-gesture-handler/pull/2116/
const RefreshControl =
  Platform.OS === 'android'
    ? createNativeWrapper(RNRefreshControl, {
        disallowInterruption: true,
        shouldCancelWhenOutside: false,
      })
    : RNRefreshControl;

type Props = {
  width: number;
  style?: StyleProp<ViewStyle>;
  columns?: number;
  data: Item[];
  isRefreshing?: boolean;
  isLoading?: boolean;
  error?: Error;
  hasMore?: boolean;
  showFinishDate?: boolean;
  hideStatusLabel?: boolean;
  extraBottomSpacing?: number;
  emptyText?: ReactNode;
  showRollbackDetail?: boolean;
  showCancelReason?: boolean;
  onRetry?: () => void;
  onItemPress?: (order: Item) => void;
  onRefresh?: () => void;
  onScrollToBottom?: () => void;
  onScrollOffsetChange?: (offset: number) => void;
} & Pick<
  FlatListProps<Item>,
  | 'ListFooterComponent'
  | 'ListHeaderComponent'
  | 'ListEmptyComponent'
  | 'contentContainerStyle'
>;

export const OrderSearchResultList = memo(
  forwardRef<OrderSearchResultListRef, Props>(
    (
      {
        width,
        style,
        data,
        columns,
        error,
        emptyText,
        isLoading,
        isRefreshing,
        hasMore,
        hideStatusLabel,
        showFinishDate,
        showRollbackDetail,
        showCancelReason,
        contentContainerStyle,
        extraBottomSpacing = 0,
        ListHeaderComponent,
        ListFooterComponent,
        ListEmptyComponent,
        onItemPress,
        onRefresh,
        onScrollToBottom,
        onScrollOffsetChange,
        onRetry,
      },
      ref,
    ) => {
      const { rpx } = useResponsiveContext();
      const refreshRef = useRef(null);
      const listRef = useRef<FlatList<Item>>(null);

      const safeAreaInsets = useSafeAreaInsets();
      const scrollY = useSharedValue(0);

      useImperativeHandle(ref, () => ({
        scrollToTop: (animated?: boolean) => {
          listRef.current?.scrollToOffset({ animated, offset: 0 });
        },
        getContentOffset: () => scrollY.value,
      }));

      const onScroll = useCallback(
        (e: NativeSyntheticEvent<NativeScrollEvent>) => {
          scrollY.value = e.nativeEvent.contentOffset.y;
          onScrollOffsetChange?.(e.nativeEvent.contentOffset.y);
        },
        [onScrollOffsetChange, scrollY],
      );

      const multiColumn = columns != null && columns > 1;

      const [cellWidth] = computeGridLayoutItemSize({
        numberOfColumns: columns ?? 1,
        gridPadding: {
          left: rpx(10),
          right: rpx(10),
          top: rpx(0),
          bottom: rpx(0),
        },
        gridWidth: width,
        aspectRatio: 4 / 3,
        cellMargin: multiColumn
          ? {
              left: rpx(kMarginMultiColumn),
              right: rpx(kMarginMultiColumn),
              top: rpx(kMarginMultiColumn),
              bottom: rpx(kMarginMultiColumn),
            }
          : {
              left: rpx(kMarginSingleColumn),
              right: rpx(kMarginSingleColumn),
              top: rpx(kMarginSingleColumn),
              bottom: rpx(kMarginSingleColumn),
            },
      });

      const { height: cellHeight } = useTaskCardMetrics();

      const cellStyle = useMemo<ViewStyle>(
        () =>
          multiColumn
            ? {
                width: cellWidth,
                height: cellHeight,
                margin: rpx(kMarginMultiColumn),
              }
            : {
                width: cellWidth,
                margin: rpx(kMarginSingleColumn),
              },
        [multiColumn, cellWidth, cellHeight, rpx],
      );

      const renderItem: ListRenderItem<Item> = useCallback(
        ({ item }) => (
          <OrderSearchResultListItem
            item={item}
            style={cellStyle}
            width={cellWidth}
            showFinishDate={showFinishDate}
            hideStatusLabel={hideStatusLabel}
            showRollbackDetail={showRollbackDetail}
            showCancelReason={showCancelReason}
            onPress={onItemPress}
          />
        ),
        [
          cellStyle,
          cellWidth,
          hideStatusLabel,
          onItemPress,
          showFinishDate,
          showRollbackDetail,
          showCancelReason,
        ],
      );

      return (
        <FlatList
          ref={listRef}
          data={kFakeEmpty ? [] : data}
          keyExtractor={keyExtractor}
          style={style}
          waitFor={Platform.OS === 'android' ? refreshRef : undefined}
          key={`columns-${columns ?? 1}`}
          numColumns={columns}
          onScroll={onScroll}
          scrollEventThrottle={1}
          contentContainerStyle={[
            {
              flexGrow: 1,
              paddingBottom: extraBottomSpacing + safeAreaInsets.bottom,
              paddingLeft: rpx(10),
              paddingRight: rpx(10),
            },
            contentContainerStyle,
          ]}
          refreshControl={
            Platform.OS === 'web' ? undefined : (
              <RefreshControl
                ref={refreshRef}
                refreshing={(isRefreshing && data.length > 0) ?? false}
                onRefresh={onRefresh}
              />
            )
          }
          onEndReachedThreshold={0.3}
          onEndReached={onScrollToBottom}
          renderItem={renderItem}
          ListEmptyComponent={
            ListEmptyComponent ??
            ((isLoading && data.length === 0 && !isRefreshing) || kFakeEmpty ? (
              <Center
                css={`
                  flex: 1;
                `}
              >
                <DefaultLoader />
              </Center>
            ) : (
              <EmptyView
                error={error}
                onRetry={onRefresh}
                message={emptyText}
              />
            ))
          }
          ListHeaderComponent={
            ListHeaderComponent ??
            (Platform.OS === 'web' && isRefreshing && data.length > 0 ? (
              <Center
                css={`
                  padding: ${rpx(15)}px;
                `}
              >
                <ActivityIndicator />
              </Center>
            ) : null)
          }
          ListFooterComponent={
            ListFooterComponent ??
            (!kFakeEmpty ? (
              <FooterView
                isLoading={isLoading && data.length > 0 && !isRefreshing}
                total={data.length}
                error={error}
                hasMore={hasMore}
                onRetry={onRetry}
                space={safeAreaInsets.bottom}
              />
            ) : null)
          }
          css={`
            flex: 1;
            padding-top: ${rpx(8)}px;
          `}
        />
      );
    },
  ),
);
